import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, LogOut, Mail, Trash2, Plus, Quote, MessageSquare, BarChart3, Save, X } from "lucide-react";
import { API_URL, BRAND } from "../lib/brand";

export default function AdminRichieste() {
  const [tab, setTab] = useState("inquiries"); // inquiries | testimonials | analytics
  const [inquiries, setInquiries] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [tForm, setTForm] = useState({ id: null, name: "", role: "", quote: "", published: true, order: 0 });
  const [showTForm, setShowTForm] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem("tv_admin_token");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!token) { navigate("/admin/login"); return; }
    load();
  }, []);

  const load = async () => {
    try {
      const [i, t, a] = await Promise.all([
        axios.get(`${API_URL}/admin/service-inquiries`, auth),
        axios.get(`${API_URL}/admin/testimonials`, auth),
        axios.get(`${API_URL}/admin/analytics/cta`, auth),
      ]);
      setInquiries(i.data.items || []);
      setTestimonials(t.data || []);
      setAnalytics(a.data);
    } catch (err) {
      if (err?.response?.status === 401) { localStorage.removeItem("tv_admin_token"); navigate("/admin/login"); }
    }
  };

  const delInquiry = async (id) => {
    if (!window.confirm("Eliminare questa richiesta?")) return;
    await axios.delete(`${API_URL}/admin/service-inquiries/${id}`, auth);
    toast.success("Eliminata"); load();
  };

  const saveT = async () => {
    if (!tForm.name || !tForm.quote) { toast.error("Nome e frase sono obbligatori"); return; }
    try {
      const p = { name: tForm.name, role: tForm.role, quote: tForm.quote, published: tForm.published, order: Number(tForm.order) };
      if (tForm.id) await axios.put(`${API_URL}/admin/testimonials/${tForm.id}`, p, auth);
      else await axios.post(`${API_URL}/admin/testimonials`, p, auth);
      toast.success("Salvata");
      setShowTForm(false);
      setTForm({ id: null, name: "", role: "", quote: "", published: true, order: 0 });
      load();
    } catch { toast.error("Errore salvataggio"); }
  };

  const delT = async (id) => {
    if (!window.confirm("Eliminare questa testimonianza?")) return;
    await axios.delete(`${API_URL}/admin/testimonials/${id}`, auth);
    toast.success("Eliminata"); load();
  };

  const logout = () => { localStorage.removeItem("tv_admin_token"); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-[#FAF8F5] p-6 md:p-10" data-testid="admin-richieste-page">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <img src={BRAND.logo} alt="" className="h-12 w-12" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768]">— Community & Contatti</p>
              <h1 className="font-display text-3xl md:text-4xl text-[#3D2140]">Richieste, Testimonianze & Analytics</h1>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/admin" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#3D2140]/15 text-sm font-semibold hover:border-[#FF2768]"><ArrowLeft size={14} /> Dashboard</Link>
            <button onClick={logout} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#3D2140]/15 text-sm font-semibold hover:border-[#FF2768]"><LogOut size={14} /> Esci</button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button onClick={() => setTab("inquiries")} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold ${tab === "inquiries" ? "bg-[#FF2768] text-white" : "bg-white border border-[#3D2140]/10 text-[#3D2140]"}`} data-testid="tab-inquiries"><MessageSquare size={14} /> Richieste ({inquiries.length})</button>
          <button onClick={() => setTab("testimonials")} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold ${tab === "testimonials" ? "bg-[#FF2768] text-white" : "bg-white border border-[#3D2140]/10 text-[#3D2140]"}`} data-testid="tab-testimonials"><Quote size={14} /> Testimonianze ({testimonials.length})</button>
          <button onClick={() => setTab("analytics")} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold ${tab === "analytics" ? "bg-[#FF2768] text-white" : "bg-white border border-[#3D2140]/10 text-[#3D2140]"}`} data-testid="tab-analytics"><BarChart3 size={14} /> Analytics CTA</button>
        </div>

        {/* INQUIRIES */}
        {tab === "inquiries" && (
          <div className="bg-white rounded-2xl border border-[#3D2140]/8 overflow-hidden">
            {inquiries.length === 0 && <p className="p-10 text-center italic text-[#3D2140]/60">Nessuna richiesta ancora. Le richieste "🌸 Richiedi info" arrivate dai carousel appariranno qui.</p>}
            {inquiries.map((r) => (
              <div key={r.id} className="p-5 border-b border-[#3D2140]/5 last:border-0" data-testid={`inquiry-${r.id}`}>
                <div className="flex items-start gap-4 flex-wrap">
                  <div className="flex-1 min-w-[260px]">
                    <p className="text-[10px] uppercase tracking-widest text-[#4E9EA5] font-bold">{r.service_label}</p>
                    <p className="font-display text-xl text-[#3D2140] mt-1">{r.name}</p>
                    <p className="text-xs text-[#3D2140]/60 mt-1">{r.email}{r.phone && ` · ${r.phone}`}</p>
                    {r.message && <p className="mt-2 text-sm italic text-[#3D2140]/70 bg-[#FAF8F5] p-2 rounded-lg border-l-2 border-[#FF2768]">"{r.message}"</p>}
                    <p className="text-[10px] text-[#3D2140]/40 mt-2">{new Date(r.created_at).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
                  </div>
                  <div className="flex gap-2">
                    <a href={`mailto:${r.email}?subject=${encodeURIComponent("Info · " + r.service_label)}`} className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-[#FF2768] text-white text-xs font-semibold hover:bg-[#e91e5e]"><Mail size={12} /> Rispondi</a>
                    <button onClick={() => delInquiry(r.id)} className="p-2 rounded-full bg-[#FAF8F5] hover:bg-red-500 hover:text-white"><Trash2 size={13} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* TESTIMONIALS */}
        {tab === "testimonials" && (
          <div>
            {showTForm ? (
              <div className="bg-white rounded-2xl p-6 border border-[#FF2768]/20 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display text-xl text-[#3D2140]">{tForm.id ? "Modifica testimonianza" : "Nuova testimonianza"}</h3>
                  <button onClick={() => { setShowTForm(false); setTForm({ id: null, name: "", role: "", quote: "", published: true, order: 0 }); }} className="p-2 rounded-full hover:bg-[#FAF8F5]"><X size={16} /></button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input value={tForm.name} onChange={(e) => setTForm({ ...tForm, name: e.target.value })} placeholder="Nome (es. Anna, mamma di Leo)" className="px-4 py-2.5 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm" data-testid="t-name" />
                  <input value={tForm.role} onChange={(e) => setTForm({ ...tForm, role: e.target.value })} placeholder="Ruolo (es. mamma, tata, HR Manager)" className="px-4 py-2.5 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm" data-testid="t-role" />
                </div>
                <textarea value={tForm.quote} onChange={(e) => setTForm({ ...tForm, quote: e.target.value })} rows={3} maxLength={400} placeholder="La frase (max 400 caratteri)" className="w-full mt-3 px-4 py-2.5 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm resize-none" data-testid="t-quote" />
                <div className="flex flex-wrap items-center gap-4 mt-4">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={tForm.published} onChange={(e) => setTForm({ ...tForm, published: e.target.checked })} className="w-4 h-4 accent-[#FF2768]" data-testid="t-published" />
                    Pubblicata sul sito
                  </label>
                  <div className="flex items-center gap-2 text-sm">
                    Ordine: <input type="number" value={tForm.order} onChange={(e) => setTForm({ ...tForm, order: e.target.value })} className="w-16 px-2 py-1 rounded bg-[#FAF8F5] border border-[#3D2140]/10 text-sm" data-testid="t-order" />
                  </div>
                  <button onClick={saveT} className="ml-auto inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF2768] text-white text-sm font-semibold hover:bg-[#e91e5e]" data-testid="t-save"><Save size={14} /> Salva</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setShowTForm(true)} className="mb-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF2768] text-white text-sm font-semibold hover:bg-[#e91e5e]" data-testid="btn-new-testimonial"><Plus size={14} /> Nuova testimonianza</button>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {testimonials.map((t) => (
                <div key={t.id} className={`p-5 rounded-2xl border ${t.published ? "bg-white border-[#3D2140]/8" : "bg-[#FAF8F5]/50 border-[#3D2140]/5 opacity-60"}`} data-testid={`testimonial-item-${t.id}`}>
                  <Quote size={16} className="text-[#FF2768] mb-2" />
                  <p className="text-sm italic text-[#3D2140]/85 leading-relaxed">"{t.quote}"</p>
                  <p className="mt-3 text-xs font-bold text-[#FF2768]">— {t.name}{t.role && <span className="text-[#3D2140]/60 font-normal">, {t.role}</span>}</p>
                  <div className="mt-4 flex gap-2">
                    <button onClick={() => { setTForm({ id: t.id, name: t.name, role: t.role, quote: t.quote, published: t.published, order: t.order }); setShowTForm(true); }} className="text-xs px-3 py-1.5 rounded-full bg-[#FAF8F5] hover:bg-[#FF2768] hover:text-white font-semibold" data-testid={`t-edit-${t.id}`}>Modifica</button>
                    <button onClick={() => delT(t.id)} className="p-1.5 rounded-full bg-[#FAF8F5] hover:bg-red-500 hover:text-white" data-testid={`t-del-${t.id}`}><Trash2 size={12} /></button>
                  </div>
                </div>
              ))}
              {testimonials.length === 0 && (
                <p className="col-span-full text-center italic text-[#3D2140]/50 py-10">Nessuna testimonianza ancora. Aggiungi 3–5 frasi vere per iniziare — appariranno sopra il carousel in `/servizi`.</p>
              )}
            </div>
          </div>
        )}

        {/* ANALYTICS */}
        {tab === "analytics" && analytics && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-white rounded-2xl p-6 border border-[#FF2768]/20">
                <p className="text-[10px] uppercase tracking-widest text-[#FF2768] font-bold">Richiedi info (click totali)</p>
                <p className="font-display italic text-5xl text-[#3D2140] mt-2">{analytics.totals["richiedi-info"] || 0}</p>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-[#4E9EA5]/30">
                <p className="text-[10px] uppercase tracking-widest text-[#4E9EA5] font-bold">Call conoscitiva (click totali)</p>
                <p className="font-display italic text-5xl text-[#3D2140] mt-2">{analytics.totals["call-conoscitiva"] || 0}</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-[#3D2140]/8 overflow-hidden">
              <p className="p-4 text-[10px] uppercase tracking-widest text-[#3D2140]/60 font-bold border-b border-[#3D2140]/5">Dettaglio per servizio</p>
              {analytics.breakdown?.length === 0 && <p className="p-8 text-center italic text-[#3D2140]/50">Ancora nessun click tracciato.</p>}
              {analytics.breakdown?.map((b) => (
                <div key={b.key} className="flex items-center justify-between p-4 border-b border-[#3D2140]/5 last:border-0" data-testid={`cta-${b.key}`}>
                  <div>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-bold text-white mr-2 ${b.cta === "richiedi-info" ? "bg-[#FF2768]" : "bg-[#4E9EA5]"}`}>{b.cta}</span>
                    <span className="text-sm text-[#3D2140]">{b.service_key || "—"}</span>
                  </div>
                  <span className="font-display text-2xl text-[#3D2140]">{b.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
